import img from "../assests/images/white-logo.png";
import img2 from "../assests/images/green-logo.png";

import React, { useEffect, useRef, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";

import {
  ChevronDown,
  Heart,
  LogOut,
  Menu,
  User,
  X,
  MessageCircle,
} from "lucide-react";

export default function Header({
  onLoginClick,
  user,
  setUser,
}) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileUserOpen, setMobileUserOpen] = useState(false);

  const dropdownRef = useRef(null);
  const mobileUserRef = useRef(null);

  const navigate = useNavigate();
  const location = useLocation();

  /*
  |--------------------------------------------------------------------------
  | HOMEPAGE / HERO HEADER
  |--------------------------------------------------------------------------
  */

  const isHomePage = location.pathname === "/";
  const transparentHeader = isHomePage && !isScrolled;

  /*
  |--------------------------------------------------------------------------
  | SCROLL DETECTION
  |--------------------------------------------------------------------------
  */

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 30);
    };

    handleScroll();

    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  /*
  |--------------------------------------------------------------------------
  | CLOSE DROPDOWNS WHEN CLICKING OUTSIDE
  |--------------------------------------------------------------------------
  */

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setDropdownOpen(false);
      }

      if (
        mobileUserRef.current &&
        !mobileUserRef.current.contains(event.target)
      ) {
        setMobileUserOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener(
        "mousedown",
        handleClickOutside
      );
    };
  }, []);

  /*
  |--------------------------------------------------------------------------
  | CLOSE MOBILE MENU WHEN ROUTE CHANGES
  |--------------------------------------------------------------------------
  */

  useEffect(() => {
    setMobileMenuOpen(false);
    setMobileUserOpen(false);
    setDropdownOpen(false);
  }, [location.pathname]);

  /*
  |--------------------------------------------------------------------------
  | PREVENT BODY SCROLL WHEN MOBILE MENU IS OPEN
  |--------------------------------------------------------------------------
  */

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileMenuOpen]);

  /*
  |--------------------------------------------------------------------------
  | HELPERS
  |--------------------------------------------------------------------------
  */

  const navigateTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
    setMobileUserOpen(false);
  };

  const handleHomeClick = () => {
    closeMobileMenu();
    navigateTop();
  };

  /*
  |--------------------------------------------------------------------------
  | LOGOUT
  |--------------------------------------------------------------------------
  */

  const logout = () => {
    localStorage.removeItem("user");

    if (setUser) {
      setUser(null);
    }

    setDropdownOpen(false);
    setMobileUserOpen(false);
    setMobileMenuOpen(false);

    navigate("/");

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  /*
  |--------------------------------------------------------------------------
  | DESKTOP NAVIGATION
  |--------------------------------------------------------------------------
  */

  const desktopNavClass = ({ isActive }) => `
    relative
    inline-flex
    items-center
    whitespace-nowrap
    px-1
    py-2
    text-[12px]
    xl:text-[13px]
    font-medium
    tracking-wide
    transition-all
    duration-300

    after:absolute
    after:left-1/2
    after:-translate-x-1/2
    after:bottom-0
    after:h-[2px]
    after:w-0
    after:rounded-full
    after:bg-[#8B0000]
    after:transition-all
    after:duration-300
    

    hover:after:w-full

    ${isActive
      ? transparentHeader
        ? "text-[#FFD54F] after:w-full"
        : "text-[#0B5552] after:w-full"
      : transparentHeader
        ? "text-white/95 hover:text-white"
        : "text-[#173D3D] hover:text-[#0B5552]"
    }
  `;

  /*
  |--------------------------------------------------------------------------
  | MOBILE NAVIGATION
  |--------------------------------------------------------------------------
  */

  const mobileNavClass = ({ isActive }) => `
    flex
    w-full
    items-center
    justify-between
    rounded-xl
    px-4
    py-3.5
    text-sm
    font-medium
    transition-all
    duration-200

    ${isActive
      ? "bg-[#F7F1E5] text-[#0B5552]"
      : "text-[#173D3D] hover:bg-[#FAF7F0] hover:text-[#0B5552]"
    }
  `;

  return (
    <>
      {/* =====================================================
          MAIN HEADER
      ===================================================== */}

      <header
        className={`
    fixed
    px-4
    left-0
    z-[1000]
    w-full
    transition-all
    duration-500

    ${isScrolled
            ? `
          top-0
          bg-white/95
          backdrop-blur-xl
          border-b
          border-[#E8E4DA]
          shadow-[0_5px_25px_rgba(10,50,55,0.10)]
        `
            : `
          top-10
          ${transparentHeader
              ? `
                bg-transparent
                border-transparent
              `
              : `
                bg-white
                border-b
                border-[#E8E4DA]
              `
            }
        `
          }
  `}
      >
        {/* Subtle dark overlay on homepage when header is transparent */}
        {transparentHeader && (
          <div className="absolute inset-0 -z-10 bg-gradient-to-b from-[#063D48]/90 via-[#063D48]/55 to-transparent" />
        )}

        <div
          className="
            mx-auto
            w-full
            max-w-[1536px]
            
          "
        >
          <div
            className={`
              flex
              items-center
              justify-between
              gap-5
              transition-all
              duration-500

              ${transparentHeader
                ? "min-h-[82px] lg:min-h-[88px]"
                : "min-h-[70px] lg:min-h-[76px]"
              }
            `}
          >
            {/* =================================================
                LOGO
            ================================================= */}

            <NavLink
              to="/"
              onClick={handleHomeClick}
              className="
    relative
    z-10
    flex
    shrink-0
    items-center
  "
              aria-label="Beach Hotels Dubai home"
            >
              <img
                src={transparentHeader ? img : img2}
                alt="Beach Hotels Dubai"
                className={`
      block
      h-auto
      object-contain
      transition-all
      duration-500
      ease-in-out

      ${transparentHeader
                    ? `
            w-[155px]
            sm:w-[175px]
            lg:w-[190px]
          `
                    : `
            w-[120px]
            sm:w-[135px]
            lg:w-[150px]
          `
                  }
    `}
              />
            </NavLink>


            {/* =================================================
                DESKTOP NAVIGATION
            ================================================= */}

            <nav
              className="
                hidden
                lg:flex
                flex-1
                items-center
                justify-center
                gap-6
                xl:gap-8
                2xl:gap-9
              "
              aria-label="Main navigation"
            >
              <NavLink
                to="/"
                onClick={navigateTop}
                className={desktopNavClass}
              >
                Home
              </NavLink>

              <NavLink
                to="/hotels"
                className={desktopNavClass}
              >
                Hotels
              </NavLink>

              <NavLink
                to="/services"
                className={desktopNavClass}
              >
                Services
              </NavLink>

              <NavLink
                to="/blogs"
                className={desktopNavClass}
              >
                Blog
              </NavLink>

              <NavLink
                to="/about-us"
                className={desktopNavClass}
              >
                About Us
              </NavLink>

              <NavLink
                to="/contact-us"
                className={desktopNavClass}
              >
                Contact
              </NavLink>

              <NavLink
                to="/wishlist"

                className={desktopNavClass}
              >
                Wishlist
                <Heart size={17} className="ml-1 text-[#8B0000]" />
              </NavLink>

            </nav>

            {/* =================================================
                DESKTOP RIGHT SIDE
            ================================================= */}

            <div
              className="
                hidden
                lg:flex
                shrink-0
                items-center
                gap-4
                xl:gap-5
              "
            >
              {/* =================================================
                  SIGN IN / USER
              ================================================= */}

              {!user ? (
                <button
                  type="button"
                  onClick={onLoginClick}
                  className={`
                    group
                    flex
                    items-center
                    gap-2
                    whitespace-nowrap
                    text-[12px]
                    font-semibold
                    transition-all
                    duration-300

                    ${transparentHeader
                      ? "text-white hover:text-[#FFD54F]"
                      : "text-[#173D3D] hover:text-[#0B5552]"
                    }
                  `}
                >
                  <User
                    size={17}
                    strokeWidth={1.8}
                    className="transition-transform duration-300 group-hover:scale-110"
                  />

                  <span>Sign In</span>
                </button>
              ) : (
                <div
                  ref={dropdownRef}
                  className="relative"
                >
                  <button
                    type="button"
                    onClick={() =>
                      setDropdownOpen((prev) => !prev)
                    }
                    className={`
                      flex
                      items-center
                      gap-2
                      rounded-full
                      px-2
                      py-1.5
                      transition-all
                      duration-300
                      ${transparentHeader
                        ? "hover:bg-white/10"
                        : "hover:bg-[#F7F3EA]"
                      }
                    `}
                  >
                    <span
                      className="
                        flex
                        h-8
                        w-8
                        items-center
                        justify-center
                        rounded-full
                        bg-[#0B5552]
                        text-xs
                        font-semibold
                        text-white
                      "
                    >
                      {user?.name
                        ?.charAt(0)
                        ?.toUpperCase() || "U"}
                    </span>

                    <span
                      className={`
                        max-w-24
                        truncate
                        text-xs
                        font-semibold
                        ${transparentHeader
                          ? "text-white"
                          : "text-[#173D3D]"
                        }
                      `}
                    >
                      {user?.name?.split(" ")[0] ||
                        "Account"}
                    </span>

                    <ChevronDown
                      size={14}
                      className={`
                        transition-transform
                        duration-200

                        ${transparentHeader
                          ? "text-white/80"
                          : "text-[#526B6A]"
                        }

                        ${dropdownOpen
                          ? "rotate-180"
                          : ""
                        }
                      `}
                    />
                  </button>

                  {/* USER DROPDOWN */}

                  {dropdownOpen && (
                    <div
                      className="
                        absolute
                        right-0
                        top-full
                        mt-3
                        w-52
                        overflow-hidden
                        rounded-2xl
                        border
                        border-[#E8E2D7]
                        bg-white
                        py-1
                        shadow-[0_18px_45px_rgba(10,52,51,0.16)]
                      "
                    >
                      <NavLink
                        to="/profile"
                        onClick={() =>
                          setDropdownOpen(false)
                        }
                        className="
                          flex
                          items-center
                          gap-3
                          px-4
                          py-3
                          text-sm
                          text-[#314F50]
                          transition
                          hover:bg-[#FAF7F0]
                          hover:text-[#0B5552]
                        "
                      >
                        <User size={17} />

                        My Profile
                      </NavLink>

                      <NavLink
                        to="/wishlist"
                        onClick={() =>
                          setDropdownOpen(false)
                        }
                        className="
                          flex
                          items-center
                          gap-3
                          px-4
                          py-3
                          text-sm
                          text-[#314F50]
                          transition
                          hover:bg-[#FAF7F0]
                          hover:text-[#0B5552]
                        "
                      >
                        <Heart size={17} />

                        Wishlist
                      </NavLink>



                      <div className="my-1 border-t border-[#EEE9DF]" />

                      <button
                        type="button"
                        onClick={logout}
                        className="
                          flex
                          w-full
                          items-center
                          gap-3
                          px-4
                          py-3
                          text-sm
                          text-red-500
                          transition
                          hover:bg-red-50
                        "
                      >
                        <LogOut size={17} />

                        Logout
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* =================================================
                  WHATSAPP HELP BUTTON
              ================================================= */}

              <a
                href="https://wa.me/971502477593"
                target="_blank"
                rel="noopener noreferrer"
                className="
    group
    relative
    inline-flex
    items-center
    justify-center
    gap-2
    whitespace-nowrap
    rounded-full
    bg-[#8B0000]
    px-4
    py-2.5
    text-[11px]
    font-semibold
    text-white
    shadow-[0_5px_18px_rgba(139,0,0,0.25)]
    transition-all
    duration-300
    ease-out
    hover:-translate-y-1
    hover:scale-[1.03]
    hover:bg-[#E8C16F]
    hover:text-[#173D3D]
    hover:shadow-[0_10px_28px_rgba(232,193,111,0.40)]
    animate-[float_3s_ease-in-out_infinite]
    before:absolute
    before:inset-0
    before:rounded-full
    before:border
    before:border-white/30
    before:opacity-0
    before:transition-all
    before:duration-300
    hover:before:scale-110
    hover:before:opacity-100
  "
              >
                <MessageCircle
                  size={15}
                  strokeWidth={2}
                  className="
      transition-transform
      duration-300
      group-hover:rotate-[-12deg]
      group-hover:scale-125
    "
                />

                <span>Need Help? Chat With Us</span>
              </a>
            </div>

            {/* =================================================
                MOBILE MENU BUTTON
            ================================================= */}

            <button
              type="button"
              onClick={() =>
                setMobileMenuOpen(true)
              }
              aria-label="Open navigation menu"
              className={`
                flex
                h-10
                w-10
                shrink-0
                items-center
                justify-center
                rounded-full
                border
                transition-all
                duration-300
                lg:hidden

                ${transparentHeader
                  ? `
                      border-white/30
                      bg-white/10
                      text-white
                      backdrop-blur-sm
                      hover:bg-white/20
                    `
                  : `
                      border-[#DCD7CC]
                      bg-white
                      text-[#0B5552]
                      hover:bg-[#F7F3EA]
                    `
                }
              `}
            >
              <Menu size={22} />
            </button>
          </div>
        </div>
      </header>

      {/* =====================================================
          MOBILE OVERLAY
      ===================================================== */}

      <div
        onClick={closeMobileMenu}
        aria-hidden={!mobileMenuOpen}
        className={`
          fixed
          inset-0
          z-[1090]
          bg-[#052F37]/60
          backdrop-blur-sm
          transition-opacity
          duration-300
          lg:hidden

          ${mobileMenuOpen
            ? "visible opacity-100"
            : "pointer-events-none invisible opacity-0"
          }
        `}
      />

      {/* =====================================================
          MOBILE SIDEBAR
      ===================================================== */}

      <aside
        aria-label="Mobile navigation"
        className={`
          fixed
          left-0
          top-0
          z-[1100]
          h-dvh
          w-[88%]
          max-w-[390px]
          overflow-y-auto
          bg-white
          shadow-2xl
          transition-transform
          duration-300
          ease-out
          lg:hidden

          ${mobileMenuOpen
            ? "translate-x-0"
            : "-translate-x-full"
          }
        `}
      >
        {/* =================================================
            MOBILE HEADER
        ================================================= */}

        <div
          className="
            flex
            items-center
            justify-between
            bg-[#073F47]
            px-5
            py-5
          "
        >
          <NavLink
            to="/"
            onClick={handleHomeClick}
            className="flex items-center"
          >
            <img
              src={img}
              alt="Beach Hotels Dubai"
              className="
                h-auto
                w-[145px]
                object-contain
              "
            />
          </NavLink>

          <button
            type="button"
            onClick={closeMobileMenu}
            aria-label="Close navigation menu"
            className="
              flex
              h-10
              w-10
              items-center
              justify-center
              rounded-full
              bg-white/10
              text-white
              transition
              hover:bg-white/20
            "
          >
            <X size={22} />
          </button>
        </div>

        {/* =================================================
            MOBILE USER
        ================================================= */}

        <div
          className="
            border-b
            border-[#EAE4D8]
            px-5
            py-4
          "
        >
          {user ? (
            <div
              ref={mobileUserRef}
              className="relative"
            >
              <button
                type="button"
                onClick={() =>
                  setMobileUserOpen(
                    (prev) => !prev
                  )
                }
                className="
                  flex
                  w-full
                  items-center
                  gap-3
                  text-left
                "
              >
                <span
                  className="
                    flex
                    h-11
                    w-11
                    shrink-0
                    items-center
                    justify-center
                    rounded-full
                    bg-[#0B5552]
                    text-base
                    font-bold
                    text-white
                  "
                >
                  {user?.name
                    ?.charAt(0)
                    ?.toUpperCase() || "U"}
                </span>

                <span className="min-w-0 flex-1">
                  <span
                    className="
                      block
                      truncate
                      text-sm
                      font-bold
                      text-[#173D3D]
                    "
                  >
                    {user?.name || "Account"}
                  </span>

                  <span
                    className="
                      block
                      text-xs
                      text-[#718080]
                    "
                  >
                    Tap to manage account
                  </span>
                </span>

                <ChevronDown
                  size={18}
                  className={`
                    text-[#718080]
                    transition-transform
                    duration-200

                    ${mobileUserOpen
                      ? "rotate-180"
                      : ""
                    }
                  `}
                />
              </button>

              {mobileUserOpen && (
                <div
                  className="
                    mt-3
                    overflow-hidden
                    rounded-xl
                    border
                    border-[#E9E3D8]
                    bg-[#F8F5EE]
                  "
                >
                  <NavLink
                    to="/profile"
                    onClick={closeMobileMenu}
                    className="
                      flex
                      items-center
                      gap-3
                      px-4
                      py-3
                      text-sm
                      text-[#314F50]
                      hover:bg-white
                      hover:text-[#0B5552]
                    "
                  >
                    <User size={17} />

                    My Profile
                  </NavLink>

                  <NavLink
                    to="/wishlist"
                    onClick={closeMobileMenu}
                    className="
                      flex
                      items-center
                      gap-3
                      px-4
                      py-3
                      text-sm
                      text-[#314F50]
                      hover:bg-white
                      hover:text-[#0B5552]
                    "
                  >
                    <Heart size={17} />

                    Wishlist
                  </NavLink>


                  <button
                    type="button"
                    onClick={logout}
                    className="
                      flex
                      w-full
                      items-center
                      gap-3
                      px-4
                      py-3
                      text-sm
                      text-red-500
                      hover:bg-red-50
                    "
                  >
                    <LogOut size={17} />

                    Logout
                  </button>
                </div>
              )}
            </div>
          ) : (
            <button
              type="button"
              onClick={() => {
                onLoginClick?.();
                closeMobileMenu();
              }}
              className="
                flex
                w-full
                items-center
                justify-center
                gap-2
                rounded-xl
                bg-[#8B0000]
                px-4
                py-3.5
                text-sm
                font-semibold
                text-[white]
                shadow-md
                transition
                hover:bg-[#D6A84D]
              "
            >
              <User size={17} />

              Sign In / Register
            </button>
          )}
        </div>

        {/* =================================================
            MOBILE NAVIGATION
        ================================================= */}

        <nav
          className="
            space-y-1
            px-4
            py-5
          "
        >
          <NavLink
            to="/"
            onClick={handleHomeClick}
            className={mobileNavClass}
          >
            Home
          </NavLink>

          <NavLink
            to="/hotels"
            onClick={closeMobileMenu}
            className={mobileNavClass}
          >
            Hotels
          </NavLink>

          <NavLink
            to="/services"
            onClick={closeMobileMenu}
            className={mobileNavClass}
          >
            Services
          </NavLink>

          <NavLink
            to="/blogs"
            onClick={closeMobileMenu}
            className={mobileNavClass}
          >
            Blog
          </NavLink>

          <NavLink
            to="/about-us"
            onClick={closeMobileMenu}
            className={mobileNavClass}
          >
            About Us
          </NavLink>

          <NavLink
            to="/contact-us"
            onClick={closeMobileMenu}
            className={mobileNavClass}
          >
            Contact
          </NavLink>

          {/* =================================================
              MOBILE WHATSAPP
          ================================================= */}

          <a
            href="https://wa.me/"
            target="_blank"
            rel="noopener noreferrer"
            className="
              mt-5
              flex
              w-full
              items-center
              justify-center
              gap-2
              rounded-xl
              bg-[#8B0000]
              px-4
              py-3.5
              text-sm
              font-semibold
              text-white
              shadow-sm
              transition
              hover:bg-[#D6A84D]
            "
          >
            <MessageCircle size={18} />

            Need Help? Chat With Us
          </a>



          {/* =================================================
              MOBILE WISHLIST
          ================================================= */}

          <NavLink
            to="/wishlist"
            onClick={closeMobileMenu}
            className="
              mt-3
              flex
              items-center
              justify-center
              gap-2
              rounded-xl
              border
              border-[#E3DDD1]
              bg-white
              px-4
              py-3.5
              text-sm
              font-semibold
              text-[#0B5552]
              transition
              hover:bg-[#F8F5EE]
            "
          >
            <Heart size={18} />

            Wishlist

            <span
              className="
                rounded-full
                bg-[#0B5552]
                px-2
                py-0.5
                text-[11px]
                text-white
              "
            >
              0
            </span>
          </NavLink>
        </nav>
      </aside>
    </>
  );
}